import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import './warmth.css';

import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { FeedbackLauncher } from './components/feedback/FeedbackLauncher';
import { AuthProvider } from './lib/auth-context';
import { prefetchMarketingCms } from './lib/cms';
import { appColorSchemeManager, resolveConcreteScheme } from './lib/color-scheme';
import { ColorSchemeSync } from './lib/ColorSchemeSync';
import { routeTree } from './routeTree.gen';
import { theme } from './theme';

const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

// Resolve to a CONCRETE scheme (never "auto") so the data-mantine-color-scheme
// attribute matches what the index.html inline bridge painted and the warm
// dark-mode CSS never flips off after mount (the FOUC fix). See color-scheme.ts.
const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root element');


createRoot(rootEl).render(
  <StrictMode>
    <MantineProvider
      theme={theme}
      defaultColorScheme={resolveConcreteScheme()}
      colorSchemeManager={appColorSchemeManager}
    >
      <Notifications position="top-right" />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ColorSchemeSync />
          <RouterProvider router={router} />
          {/* Public entry point disabled for launch (2026-08-26); re-enabled
              admin-only (2026-09-07, on Richie's request) — FeedbackLauncher
              itself gates on activeHat === 'admin' and renders nothing
              otherwise, so this is safe to mount unconditionally. */}
          <FeedbackLauncher />
        </AuthProvider>
      </QueryClientProvider>
    </MantineProvider>
  </StrictMode>,
);

// Warm the CMS cache for the other marketing pages once the first page is
// interactive, so a client-side navigation doesn't flash the registry defaults
// before that page's bodies load (the worker only injects the hard-loaded
// page's blob). Best-effort, idle-scheduled. See prefetchMarketingCms in cms.ts.
{
  const warmCms = (): void => prefetchMarketingCms(queryClient);
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(warmCms, { timeout: 3000 });
  } else {
    window.setTimeout(warmCms, 1500);
  }
}
